import { useMemo } from 'react';
import {
  PaginationOptionsWithSortArray,
  useVendorVectorContracts,
  useVectorContracts,
  VectorApiContractStatus,
  VectorContractDto,
} from '@sib/api';
import { PDF_EXTENSION } from '@sib/config';
import { useNotification } from '@sib/notifications';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  mapColumnToColumnWithMinSize,
} from '@sib/table';
import { Button, TableBadge, Tooltip } from '@sib/ui';
import { Search, Download } from '@sib/ui/icons';
import { DOCUMENTS_DATE_FORMAT } from '@sib/utils';
import { NO_DATA_PLACEHOLDER } from '@spendbrain/config/constants';
import { useDownloadBlobFile } from '@spendbrain/hooks';
import { useAppTranslation } from '@spendbrain/modules/vendors/hooks';
import { format } from 'date-fns';

export type ContractScope = 'organization' | 'vendor';

enum IsVetted {
  Yes = 'true',
  No = 'false',
}

export interface ContractPreview {
  fileUrl: string;
  title?: string | null;
}

interface UseContractsProps extends PaginationOptionsWithSortArray {
  organizationId: string;
  vendorId?: string;
  scope?: ContractScope;
}

export const useContracts = ({
  organizationId,
  vendorId,
  scope = 'vendor',
  filter,
  sort,
  page,
  size,
}: UseContractsProps) => {
  const vendorContractsQuery = useVendorVectorContracts(
    organizationId,
    scope === 'vendor' ? (vendorId ?? '') : '',
    {
      page,
      size,
      sort,
      filter,
    }
  );

  const organizationContractsQuery = useVectorContracts(
    scope === 'organization' ? organizationId : '',
    {
      page,
      size,
      sort,
      filter,
    }
  );

  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    refetch: refetchContracts,
  } = scope === 'organization'
    ? organizationContractsQuery
    : vendorContractsQuery;

  return {
    refetchContracts,
    data: data?.items || [],
    isLoading: isLoading || isFetching || isRefetching,
    rowCount: data?.totalItems || 0,
  };
};

interface useContractsColumnsProps {
  setContractPreview: (contractPreview: ContractPreview) => void;
}

export const useContractsColumns = ({
  setContractPreview,
}: useContractsColumnsProps) => {
  const { t } = useAppTranslation();
  const { open } = useNotification();

  const columnHelper = createColumnHelper<VectorContractDto>();
  const { downloadBlobFile } = useDownloadBlobFile({
    onError: () => {
      open({
        type: 'error',
        message: t('components.contracts.pdfModal.errorMessage'),
        description: t('components.contracts.pdfModal.errorDescription'),
      });
    },
  });
  const isVettedOptions = useContractIsVettedOptions();
  const contractStatusOptions = useContractStatusOptions();

  const columns = useMemo(
    () =>
      (
        [
          columnHelper.accessor('id', {
            header: t('components.contracts.columns.contractId'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => {
              const contractId = info.getValue() || NO_DATA_PLACEHOLDER;

              return (
                <span className="text-primary font-bold">{contractId}</span>
              );
            },
          }),
          columnHelper.accessor('negotiationNo', {
            header: t('components.contracts.columns.negotationNumber'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
          }),
          columnHelper.accessor('locationName', {
            header: t('components.contracts.columns.propertyName'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
          }),
          columnHelper.accessor('vendorName', {
            header: t('components.contracts.columns.vendorName'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
          }),
          columnHelper.accessor('isVetted', {
            header: t('components.contracts.columns.isVetted'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'single_select',
                options: isVettedOptions,
              },
            },
            cell: info => {
              const value = info.getValue();
              if (value === undefined) return NO_DATA_PLACEHOLDER;

              return value ? t('yes') : t('no');
            },
          }),
          columnHelper.accessor('effectiveDate', {
            header: t('components.contracts.columns.effectiveDate'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'date',
              },
            },
            cell: info => {
              const value = info.getValue();
              if (!value) return NO_DATA_PLACEHOLDER;

              return format(value, DOCUMENTS_DATE_FORMAT);
            },
          }),
          columnHelper.accessor('status', {
            header: t('components.contracts.columns.contractStatus'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'single_select',
                options: contractStatusOptions,
              },
            },
            cell: info => {
              const value = info.getValue();
              if (!value) return NO_DATA_PLACEHOLDER;
              const contractStatus = contractStatusOptions.find(
                option => option.value === value
              );

              return <TableBadge>{contractStatus?.label || value}</TableBadge>;
            },
          }),
          {
            id: 'actions',
            enableResizing: false,
            size: 90,
            sortable: false,
            pinnable: false,
            header: () => (
              <span className="mb-8 flex w-full items-start justify-center">
                {t('more')}
              </span>
            ),
            cell: info => {
              const fileUrl = info.row.original.fileUrl;
              const negotiationNo = info.row.original.negotiationNo;
              const hasFileUrl = !!fileUrl;

              return (
                <Tooltip
                  content={
                    !hasFileUrl &&
                    t('components.contracts.pdfModal.disabledTooltip')
                  }
                  TooltipContentProps={{ align: 'end' }}
                  TooltipTriggerProps={{ asChild: true }}
                >
                  <div className="flex items-center justify-end gap-2 pr-2">
                    <Button
                      variant="ghost"
                      disabled={!hasFileUrl}
                      className="text-primary mt-0.5 size-5.5 p-0"
                      {...(hasFileUrl && {
                        onClick: () =>
                          setContractPreview({
                            fileUrl,
                            title: negotiationNo,
                          }),
                      })}
                    >
                      <Search />
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={!hasFileUrl}
                      className="text-primary size-6 p-0"
                      {...(hasFileUrl && {
                        onClick: () =>
                          downloadBlobFile(
                            fileUrl,
                            `${negotiationNo}${PDF_EXTENSION}`
                          ),
                      })}
                    >
                      <Download />
                    </Button>
                  </div>
                </Tooltip>
              );
            },
          },
        ] as AccessorKeyColumnDef<VectorContractDto>[]
      ).map(mapColumnToColumnWithMinSize),
    [
      columnHelper,
      t,
      isVettedOptions,
      contractStatusOptions,
      setContractPreview,
      downloadBlobFile,
    ]
  );

  return columns;
};

const useContractIsVettedOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      { value: IsVetted.Yes, label: t('yes') },
      {
        value: IsVetted.No,
        label: t('no'),
      },
    ],
    [t]
  );
};

const useContractStatusOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      {
        value: VectorApiContractStatus.BillProcessingOnly,
        label: t('components.contracts.contractStatus.billProcessingOnly'),
      },
      {
        value: VectorApiContractStatus.FullyExecuted,
        label: t('components.contracts.contractStatus.fullyExecuted'),
      },
      {
        value: VectorApiContractStatus.Archived,
        label: t('components.contracts.contractStatus.archived'),
      },
    ],
    [t]
  );
};
